import { useRef, useState } from "react";
import { motion, useInView, useReducedMotion, useTransform } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { POSTERS } from "@/data/posters";
import { posterDistance, usePosterCarousel } from "@/hooks/usePosterCarousel";
import { EASE } from "@/lib/motion";

const number = (value) => String(value).padStart(2, "0");

const PosterArtwork = ({ poster, index, count, carousel, onMissing }) => {
  const { position, spacing, depth, active, reduced, select } = carousel;
  const distance = useTransform(position, (value) => posterDistance(index, value, count));
  const transform = useTransform(() => {
    const d = distance.get();
    const a = Math.abs(d);
    return `translate(-50%, -50%) translate3d(${d * spacing.get()}px, 0, ${-a * depth.get()}px) rotateY(${reduced ? 0 : d * -7}deg) scale(${Math.max(0.68, 1 - a * 0.12)})`;
  });
  const opacity = useTransform(distance, (d) => {
    const a = Math.abs(d);
    return a > 2.4 ? 0 : a > 2 ? (2.4 - a) / 0.4 * 0.5 : 1 - a * 0.25;
  });
  const zIndex = useTransform(distance, (d) => 20 - Math.round(Math.abs(d) * 5));
  const visibility = useTransform(distance, (d) => Math.abs(d) > 2.4 ? "hidden" : "visible");
  const nearby = Math.abs(posterDistance(index, active, count)) <= 2;

  return (
    <motion.button
      type="button" className="poster-artwork" style={{ transform, opacity, zIndex, visibility }}
      data-testid={`poster-select-${poster.index}`} data-active={index === active}
      data-cursor={index === active ? "drag" : "view"}
      aria-label={`Select ${poster.filename}, poster ${index + 1} of ${count}`}
      aria-pressed={index === active} tabIndex={index === active ? 0 : -1}
      onClick={() => select(index)}
    >
      {nearby && <img
        data-testid={`poster-image-${poster.index}`} src={poster.source}
        alt={poster.title || poster.filename.replace(/\.[^.]+$/, "")}
        loading={index === active ? "eager" : "lazy"}
        fetchPriority={index === active ? "high" : "auto"} decoding="async"
        draggable={false} onError={() => onMissing(poster.id)}
      />}
    </motion.button>
  );
};

const ExhibitionControls = ({ posters, carousel }) => (
  <div className="poster-navigation" data-testid="poster-navigation">
    <div className="poster-caption" aria-live="polite" aria-atomic="true">
      <p data-testid="poster-counter" className="label text-[#e5e5e5]">
        <span className="text-[#d4ff00]">{number(carousel.active + 1)}</span>
        <span className="px-3 text-[#7a7a7a]">/</span>{number(posters.length)}
      </p>
      <p data-testid="poster-category" className="mt-2 label text-[#a3a3a3]">GRAPHIC DESIGN</p>
      <p data-testid="poster-filename" className="poster-filename">{posters[carousel.active].filename}</p>
    </div>
    <div className="poster-pagination" role="group" aria-label="Choose a poster">
      {posters.map((poster, index) => (
        <button key={poster.id} type="button" data-testid={`poster-indicator-${poster.index}`}
          className="poster-indicator" aria-label={`Go to poster ${index + 1}: ${poster.filename}`}
          aria-current={index === carousel.active ? "true" : undefined}
          onClick={() => carousel.select(index)}>
          <span />
        </button>
      ))}
    </div>
    <div className="poster-arrows">
      <button type="button" data-testid="poster-prev-btn" aria-label="Previous poster"
        className="poster-arrow" disabled={posters.length < 2} onClick={() => carousel.move(-1)}>
        <ArrowLeft size={18} strokeWidth={1.5} />
      </button>
      <button type="button" data-testid="poster-next-btn" aria-label="Next poster"
        className="poster-arrow" disabled={posters.length < 2} onClick={() => carousel.move(1)}>
        <ArrowRight size={18} strokeWidth={1.5} />
      </button>
    </div>
  </div>
);

const ExhibitionGallery = ({ posters, onMissing }) => {
  const carousel = usePosterCarousel(posters.length);
  return (
    <div data-testid="poster-gallery" onKeyDown={carousel.onKeyDown}
      role="region" aria-roledescription="carousel" aria-label="Graphic design posters">
      <div ref={carousel.stage} className="poster-stage" tabIndex={0} data-cursor="drag"
        data-testid="poster-stage" aria-label="Drag or swipe to explore posters"
        aria-describedby="poster-help" {...carousel.pointerHandlers}>
        {posters.map((poster, index) => (
          <PosterArtwork key={poster.id} poster={poster} index={index} count={posters.length}
            carousel={carousel} onMissing={onMissing} />
        ))}
      </div>
      <ExhibitionControls posters={posters} carousel={carousel} />
      <p id="poster-help" data-testid="poster-help" className="poster-help label text-[#7a7a7a]">
        <span className="hidden md:inline">DRAG TO EXPLORE · SCROLL / ← →</span>
        <span className="md:hidden">SWIPE TO EXPLORE · TAP TO SELECT</span>
      </p>
    </div>
  );
};

export const PosterExhibition = () => {
  const ref = useRef(null);
  const near = useInView(ref, { once: true, margin: "300px 0px" });
  const reduced = useReducedMotion();
  const [posters, setPosters] = useState(POSTERS);
  const onMissing = (id) => setPosters((items) => items.filter((poster) => poster.id !== id));
  if (!posters.length) return null;

  return (
    <section ref={ref} id="posters" data-testid="poster-exhibition"
      className="poster-exhibition relative border-t border-[#1f1f1f] py-20 md:py-32"
      aria-labelledby="poster-heading">
      <div className="mx-auto max-w-[1700px] px-5 md:px-10">
        <motion.header initial={reduced ? false : { opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.85, ease: EASE }} className="poster-heading">
          <p data-testid="poster-section-label" className="label text-[#d4ff00]">02 / GRAPHIC DESIGN</p>
          <h2 id="poster-heading" data-testid="poster-heading"
            className="font-display text-base font-extrabold uppercase tracking-[-0.03em] text-[#e5e5e5] md:text-lg">
            GRAPHIC DESIGN WORK
          </h2>
        </motion.header>
        <motion.div initial={reduced ? false : { opacity: 0, scale: 0.97, y: 20 }}
          whileInView={{ opacity: 1, scale: 1, y: 0 }} viewport={{ once: true, amount: 0.08 }}
          transition={{ duration: 1.1, ease: EASE }} className="poster-gallery-space">
          {near && <ExhibitionGallery key={posters.map((p) => p.id).join("|")}
            posters={posters} onMissing={onMissing} />}
        </motion.div>
      </div>
    </section>
  );
};
